/**
 * Trunnion AI - Breadcrumbs Hook
 * Derives breadcrumb navigation segments from the current route location.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbSegment {
  /** Display label for this breadcrumb segment. */
  label: string;
  /** Absolute path this segment links to. */
  path: string;
  /** Whether this is the currently active (last) segment. */
  isActive: boolean;
}

/**
 * Mapping of URL path segments to human-readable labels.
 * Top-level route segments.
 */
const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  workflows: 'Workflows',
  executions: 'Executions',
  agents: 'Agents',
  audit: 'Audit Log',
  admin: 'Admin',
  templates: 'Templates',
  settings: 'Settings',
  missions: 'Missions',
  search: 'Search',
};

/**
 * Admin sub-route segment labels.
 */
const ADMIN_SUB_LABELS: Record<string, string> = {
  policies: 'Policies',
  tools: 'Tools',
  llm: 'LLM Providers',
  users: 'Users',
  simulator: 'Policy Simulator',
};

/**
 * Special action segment labels.
 */
const ACTION_LABELS: Record<string, string> = {
  new: 'New',
  edit: 'Edit',
  chat: 'Chat',
};

/**
 * UUID v4 detection pattern.
 * Matches standard 8-4-4-4-12 hexadecimal UUID format.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolves a URL path segment into a human-readable label.
 * Considers the segment's position and its parent segment for context.
 */
function resolveSegmentLabel(
  segment: string,
  parentSegment: string | null,
): string {
  // Check for action labels first (new, edit, chat)
  if (ACTION_LABELS[segment]) {
    return ACTION_LABELS[segment];
  }

  // Check admin sub-routes
  if (parentSegment === 'admin' && ADMIN_SUB_LABELS[segment]) {
    return ADMIN_SUB_LABELS[segment];
  }

  // Check top-level labels
  if (SEGMENT_LABELS[segment]) {
    return SEGMENT_LABELS[segment];
  }

  // UUID segments -- show first 8 characters
  if (UUID_PATTERN.test(segment)) {
    return segment.slice(0, 8);
  }

  // Fallback: capitalize the segment
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

/**
 * Hook that derives breadcrumb segments from the current URL path.
 *
 * @returns Object containing an array of `BreadcrumbSegment` items.
 *
 * @example
 * ```tsx
 * const { segments } = useBreadcrumbs();
 * // URL: /admin/policies
 * // segments: [
 * //   { label: 'Admin', path: '/admin', isActive: false },
 * //   { label: 'Policies', path: '/admin/policies', isActive: true },
 * // ]
 * ```
 */
export function useBreadcrumbs(): { segments: BreadcrumbSegment[] } {
  const location = useLocation();

  const segments = useMemo(() => {
    const pathSegments = location.pathname
      .split('/')
      .filter((seg) => seg.length > 0);

    if (pathSegments.length === 0) {
      return [];
    }

    return pathSegments.map((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const parentSegment = index > 0 ? pathSegments[index - 1] : null;
      const isActive = index === pathSegments.length - 1;

      return {
        label: resolveSegmentLabel(segment, parentSegment),
        path,
        isActive,
      };
    });
  }, [location.pathname]);

  return { segments };
}
